import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Mapa de rutas a nombres amigables en español
const PAGE_NAMES = {
  '/dashboard': 'Dashboard',
  '/operations': 'Dashboard de Operaciones',
  '/inventory': 'Inventario',
  '/categories': 'Categorías',
  '/locations': 'Ubicaciones',
  '/responsibles': 'Responsables',
  '/movements': 'Movimientos',
  '/responsivas': 'Responsivas',
  '/equipment-tracking': 'Rastreo de Equipo',
  '/calendar': 'Calendario',
  '/reports': 'Reportes',
  '/templates': 'Plantillas',
  '/manual': 'Manual de Usuario',
  '/faq': 'Ayuda / FAQ',
  '/version': 'Historial de Versiones',
  '/users': 'Gestión de Usuarios',
  '/permissions': 'Permisos',
  '/audit': 'Auditoría',
  '/style': 'Configuración de Estilo',
  '/backup': 'Backup y Restauración',
  '/email': 'Configuración de Correo',
  '/announcements': 'Anuncios',
  '/debugger': 'Debugger',
  '/documentacion': 'Documentación',
  '/profile': 'Mi Perfil',
  '/login': 'Inicio de Sesión'
};

// Función para obtener descripción amigable del elemento
function getElementDescription(element) {
  if (!element) return 'Elemento desconocido';
  
  // Prioridad: data-testid > aria-label > title > innerText > tagName
  const testId = element.getAttribute('data-testid');
  if (testId) {
    // Convertir kebab-case a texto legible
    return testId.replace(/-/g, ' ').replace(/btn$/i, '').replace(/button$/i, '').trim();
  }
  
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel;
  
  const title = element.getAttribute('title');
  if (title) return title;
  
  // Buscar texto visible (limitar a 60 chars)
  const text = element.innerText?.trim();
  if (text && text.length > 0 && text.length <= 60) {
    return text;
  }
  if (text && text.length > 60) {
    return text.substring(0, 57) + '...';
  }
  
  // Fallback: tag + tipo
  const tagName = element.tagName.toLowerCase();
  const type = element.getAttribute('type');
  const role = element.getAttribute('role');
  
  if (role) return `${role}`;
  if (type) return `${tagName} (${type})`;
  return tagName;
}

// Función para determinar el tipo de acción
function getActionType(element) {
  if (!element) return 'click';
  
  const role = element.getAttribute('role');
  const tagName = element.tagName.toLowerCase();
  
  if (role === 'tab') return 'tab_change';
  if (role === 'menuitem') return 'menu_click';
  if (role === 'switch' || role === 'checkbox') return 'toggle';
  if (tagName === 'a') return 'link_click';
  if (tagName === 'select') return 'select_change';
  if (element.type === 'submit') return 'form_submit';
  
  return 'click';
}

// Hook principal para trackear toda la actividad del usuario
export const useActivityTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastPathRef = useRef(location.pathname);
  const clickDebounceRef = useRef({ element: null, time: 0 });
  const sessionStartRef = useRef(Date.now());

  // Función interna para enviar tracking
  const sendTrack = useCallback(async (data) => {
    if (!user) return;
    
    try {
      await api.post('/activity/log', {
        ...data,
        session_duration: Math.floor((Date.now() - sessionStartRef.current) / 1000)
      });
    } catch (error) {
      // Silently fail - no interrumpir experiencia
      console.debug('Activity tracking failed:', error);
    }
  }, [user]);

  // Trackear visita a página
  const trackPageVisit = useCallback(async (path, fromPath = null) => {
    const pageName = PAGE_NAMES[path] || path;
    await sendTrack({
      action_type: 'navigation',
      action: `Visitó página: ${pageName}`,
      page: path,
      element: 'page',
      details: {
        page_name: pageName,
        from_page: fromPath,
        referrer: document.referrer || 'direct',
        timestamp: new Date().toISOString()
      }
    });
  }, [sendTrack]);

  // Trackear clic en elemento
  const trackClick = useCallback(async (elementId, elementText, context = {}) => {
    await sendTrack({
      action_type: context.actionType || 'click',
      action: `Clic en: ${elementText}`,
      page: location.pathname,
      element: elementId,
      details: {
        element_text: elementText,
        ...context
      }
    });
  }, [sendTrack, location.pathname]);

  // Trackear acción específica
  const trackAction = useCallback(async (action, entity, details = {}) => {
    await sendTrack({
      action_type: 'action',
      action: action,
      page: location.pathname,
      element: entity,
      details: details
    });
  }, [sendTrack, location.pathname]);

  // Trackear búsqueda
  const trackSearch = useCallback(async (searchTerm, context = 'general') => {
    await sendTrack({
      action_type: 'search',
      action: `Búsqueda: "${searchTerm}"`,
      page: location.pathname,
      element: 'search_input',
      details: {
        search_term: searchTerm,
        context: context
      }
    });
  }, [sendTrack, location.pathname]);

  // Trackear filtro aplicado
  const trackFilter = useCallback(async (filterName, filterValue) => {
    await sendTrack({
      action_type: 'filter',
      action: `Filtró por ${filterName}: ${filterValue}`,
      page: location.pathname,
      element: 'filter',
      details: {
        filter_name: filterName,
        filter_value: filterValue
      }
    });
  }, [sendTrack, location.pathname]);

  // Trackear descarga
  const trackDownload = useCallback(async (filename, fileType) => {
    await sendTrack({
      action_type: 'download',
      action: `Descargó: ${filename}`,
      page: location.pathname,
      element: 'download_button',
      details: {
        filename: filename,
        file_type: fileType
      }
    });
  }, [sendTrack, location.pathname]);

  // Trackear apertura de modal
  const trackModal = useCallback(async (modalName, action = 'open') => {
    await sendTrack({
      action_type: 'modal',
      action: `${action === 'open' ? 'Abrió' : 'Cerró'} modal: ${modalName}`,
      page: location.pathname,
      element: 'modal',
      details: {
        modal_name: modalName,
        modal_action: action
      }
    });
  }, [sendTrack, location.pathname]);

  // Efecto para trackear navegación automáticamente
  useEffect(() => {
    if (user && location.pathname !== '/login') {
      const previousPath = lastPathRef.current;
      if (location.pathname !== previousPath) {
        trackPageVisit(location.pathname, previousPath);
        lastPathRef.current = location.pathname;
      }
    }
  }, [location.pathname, user, trackPageVisit]);

  // Listener global para clics
  useEffect(() => {
    if (!user) return;

    const handleClick = (event) => {
      // Encontrar el elemento trackeable más cercano
      const target = event.target.closest('button, a, [data-testid], [role="button"], [role="tab"], [role="menuitem"], [role="switch"], [data-track]');
      if (!target) return;

      // Ignorar elementos marcados como no-track
      if (target.closest('.no-track')) return;

      // Debounce: evitar múltiples logs del mismo clic
      const now = Date.now();
      if (clickDebounceRef.current.element === target && now - clickDebounceRef.current.time < 300) {
        return;
      }
      clickDebounceRef.current = { element: target, time: now };

      // Obtener información del elemento
      const elementId = target.getAttribute('data-testid') || target.id || target.className?.split(' ')[0] || 'unknown';
      const elementText = getElementDescription(target);
      const actionType = getActionType(target);
      
      trackClick(elementId, elementText, {
        actionType: actionType,
        tagName: target.tagName?.toLowerCase(),
        href: target.href || null,
        role: target.getAttribute('role'),
        testId: target.getAttribute('data-testid')
      });
    };

    // Usar capture para interceptar antes de propagación
    document.addEventListener('click', handleClick, { capture: true, passive: true });
    
    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [user, trackClick]);

  // Listener para teclas importantes (Enter en submit, Escape)
  useEffect(() => {
    if (!user) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        const target = event.target;
        if (target.type === 'submit' || target.closest('form button[type="submit"]')) {
          sendTrack({
            action_type: 'keyboard',
            action: 'Envió formulario con Enter',
            page: location.pathname,
            element: 'form',
            details: { key: 'Enter', form_submit: true }
          });
        }
      } else if (event.key === 'Escape') {
        // Solo trackear si hay un modal abierto
        const openModal = document.querySelector('[role="dialog"]:not([aria-hidden="true"])');
        if (openModal) {
          sendTrack({
            action_type: 'keyboard',
            action: 'Cerró modal con Escape',
            page: location.pathname,
            element: 'modal',
            details: { key: 'Escape' }
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, { passive: true });
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [user, location.pathname, sendTrack]);

  // Trackear cuando el usuario sale de la página
  useEffect(() => {
    if (!user) return;

    const handleBeforeUnload = () => {
      const pageName = PAGE_NAMES[location.pathname] || location.pathname;
      const sessionDuration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      
      // Usar sendBeacon para garantizar envío
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('token');
        
        if (navigator.sendBeacon && token) {
          const blob = new Blob([JSON.stringify({
            action_type: 'session_end',
            action: `Salió de la aplicación desde: ${pageName}`,
            page: location.pathname,
            element: 'window',
            details: {
              event: 'beforeunload',
              session_duration_seconds: sessionDuration,
              page_name: pageName
            }
          })], { type: 'application/json' });
          
          // Nota: sendBeacon no permite headers personalizados,
          // el backend necesitará manejar esto o usar cookies
          navigator.sendBeacon(`${API_URL}/api/activity/beacon`, blob);
        }
      } catch (e) {
        // Silently fail
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user, location.pathname]);

  // Trackear visibilidad de la página (cuando el usuario cambia de tab)
  // Con throttle para evitar spam de eventos EN LA MISMA PÁGINA
  const lastVisibilityRef = useRef({ time: 0, hidden: null, page: null });
  
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      const now = Date.now();
      const lastEvent = lastVisibilityRef.current;
      const currentPage = location.pathname;
      
      // Throttle: mínimo 30 segundos entre eventos del mismo tipo EN LA MISMA PÁGINA
      // Si cambió de página, siempre registrar
      const isSamePage = lastEvent.page === currentPage;
      const isSameState = lastEvent.hidden === document.hidden;
      const isWithinThrottle = now - lastEvent.time < 30000;
      
      if (isSamePage && isSameState && isWithinThrottle) {
        return;
      }
      
      // Actualizar referencia
      lastVisibilityRef.current = { time: now, hidden: document.hidden, page: currentPage };
      
      const pageName = PAGE_NAMES[currentPage] || currentPage;
      
      if (document.hidden) {
        sendTrack({
          action_type: 'visibility',
          action: `Ocultó la pestaña (${pageName})`,
          page: currentPage,
          element: 'window',
          details: { hidden: true }
        });
      } else {
        sendTrack({
          action_type: 'visibility',
          action: `Volvió a la pestaña (${pageName})`,
          page: currentPage,
          element: 'window',
          details: { hidden: false }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, location.pathname, sendTrack]);

  return { 
    trackClick, 
    trackAction, 
    trackPageVisit,
    trackSearch,
    trackFilter,
    trackDownload,
    trackModal
  };
};

export default useActivityTracker;
